import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import WhatsNewBanner from "../components/WhatsNewBanner";
import {
  TrendingUp,
  ShieldCheck,
  Sparkles,
  FileSpreadsheet,
  Scale,
  Receipt,
  ArrowRight,
  Globe,
  ClipboardPaste,
  BarChart3,
  BookOpenCheck,
  Store,
  Utensils,
  Hammer,
  Truck,
  HeartPulse,
  Wrench,
  Hotel,
  ShoppingBag,
  Cpu,
  Briefcase,
  Sprout,
  Plus,
  Minus,
} from "lucide-react";
import { useState } from "react";

const FAQS = [
  {
    q: "Is CFO Grade really free?",
    a: "Yes — the full analysis, all 24+ ratios, the letter grade, and industry benchmarks are completely free with no signup required. Optional paid features ($19 single PDF, $29 Pro Pass with AI memo) exist for users who want more, but you never need to pay to see your grade.",
  },
  {
    q: "Do I need to create an account?",
    a: "No account, no email, no password. You enter your numbers and get your report instantly. Pro entitlements are bound to your device with a restore link you can save or email yourself for cross-device access.",
  },
  {
    q: "Will my financial data be shared or sold?",
    a: "Never. Your numbers stay on your device unless you explicitly opt in to anonymized peer benchmarking — and even then we only store the ratios (no business name, no monetary amounts). We don't sell data and there are no marketing pixels on the site.",
  },
  {
    q: "What if I don't have all the numbers?",
    a: "Leave any field blank — CFO Grade computes whatever ratios it can with the inputs you give. As few as six inputs (revenue, COGS, EBITDA proxies, current assets/liabilities, debt) get you a meaningful report.",
  },
  {
    q: "What is a 'good' EBITDA margin?",
    a: "Most healthy small businesses run an EBITDA margin between 10% and 20%. Service businesses and SaaS often run higher (20–35%+); restaurants and retail typically run 10–15%. CFO Grade scores your EBITDA margin against industry-specific benchmarks instead of a generic threshold.",
  },
  {
    q: "What DSCR do lenders look for?",
    a: "Most SBA and commercial lenders require a Debt Service Coverage Ratio (DSCR) of at least 1.25×. Construction sureties often require 1.5× or higher. Below 1.0× means your EBITDA doesn't cover your annual debt payments — a major red flag.",
  },
  {
    q: "How is the letter grade calculated?",
    a: "CFO Grade scores 24+ ratios across profitability, liquidity, leverage, and efficiency — each weighted by industry. The composite score maps to a letter grade from A+ (institutional quality) to F (distress). The full methodology is on the /learn page.",
  },
  {
    q: "How does CFO Grade estimate what my business is worth?",
    a: "Every report includes an Enterprise Value range built from your industry's typical EV/EBITDA multiple applied to your EBITDA, adjusted for size (Duff & Phelps size-discount methodology) and quality (your CFO Grade). The paid report unlocks the lender view vs. buyer view breakdown, implied equity to owners, sensitivity analysis, and full source-cited methodology. The dedicated valuation calculator lives at /valuation with industry-specific pages at /valuation/:industry.",
  },
  {
    q: "Why does my valuation change when I rerun the report?",
    a: "The range moves with three things: your EBITDA (the biggest driver), your revenue size band (which sets the size discount), and your CFO Grade quality factor. When you run a second report in a later period, the Enterprise Value delta is shown prominently — 'You built $220,000 of value since Q3' or 'You lost $180,000 this quarter' — so you can watch the number that actually matters to lenders and buyers over time.",
  },
];

const INDUSTRIES = [
  { icon: Utensils, label: "Restaurants" },
  { icon: Store, label: "Retail" },
  { icon: ShoppingBag, label: "E-commerce" },
  { icon: Cpu, label: "SaaS" },
  { icon: Briefcase, label: "Pro Services" },
  { icon: Hammer, label: "Construction" },
  { icon: Truck, label: "Logistics" },
  { icon: HeartPulse, label: "Healthcare" },
  { icon: Hotel, label: "Hospitality" },
  { icon: Wrench, label: "Trades" },
  { icon: Sprout, label: "Agriculture" },
  { icon: Globe, label: "+5 more" },
];

const VOICES = [
  {
    quote:
      "I ran a quick health check on twelve client P&Ls in under an hour. The letter grade is the part my clients actually understand.",
    role: "Bookkeeper, Texas",
  },
  {
    quote:
      "Our DSCR was 1.1× — we would have walked into the bank and embarrassed ourselves. Fixed it in two months before applying.",
    role: "Restaurant owner, Ontario",
  },
  {
    quote:
      "Used it to sanity-check the diligence pack on a small acquisition. Saved me an hour of building my own model.",
    role: "M&A analyst",
  },
];

const FEATURES = [
  { icon: TrendingUp, title: "Every margin, calculated", body: "Gross, operating, EBITDA, and net margins side-by-side, with industry-aware thresholds." },
  { icon: Scale, title: "Debt and risk, in plain English", body: "DSCR, interest coverage, debt-to-EBITDA and a clear risk rating that bankers actually use." },
  { icon: Receipt, title: "Liquidity and efficiency", body: "Current ratio, quick ratio, cash conversion cycle, working capital — without the spreadsheet pain." },
  { icon: Sparkles, title: "AI advisor narrative", body: "A senior-CFO-style memo highlighting risks and the moves that move the needle next quarter." },
  { icon: FileSpreadsheet, title: "Type or upload", body: "Use a friendly form, or drop in a CSV, Excel, or PDF of your P&L and balance sheet." },
  { icon: ShieldCheck, title: "Private by default", body: "No accounts, no marketing pixels, no funnels. You sign an acknowledgement and get to work." },
  { icon: Globe, title: "Any currency, any country", body: "USD, EUR, INR, BRL, ZAR — pick yours. Ratios and benchmarks are universal." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  return (
    <div className="bg-grain relative min-h-screen">
      <WhatsNewBanner />
      <Header />
      <Hero onStart={() => navigate("/analyze")} />
      <HowItWorks />
      <IndustryStrip />
      <Features />
      <Voices />
      <Persona />
      <FAQ />
      <FooterCta onStart={() => navigate("/analyze")} />
      <SiteFooter />
    </div>
  );
}

function Header() {
  return (
    <header className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 pt-8 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
        <BrandWordmark size="md" />
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm text-[#5C665D]">
        <a href="#how" className="hover:text-[#1F382E] transition-colors">How it works</a>
        <a href="#features" className="hover:text-[#1F382E] transition-colors">What it measures</a>
        <a href="#who" className="hover:text-[#1F382E] transition-colors">Who it's for</a>
        <Link to="/learn" className="hover:text-[#1F382E] transition-colors">Glossary</Link>
        <Link to="/blog" className="hover:text-[#1F382E] transition-colors" data-testid="nav-blog">Blog</Link>
        <a href="#faq" className="hover:text-[#1F382E] transition-colors">FAQ</a>
        <Link to="/leaderboard" className="hover:text-[#1F382E] transition-colors">Leaderboard</Link>
        <Link to="/cfo-books" className="hover:text-[#C26D5C] text-[#C26D5C] transition-colors" data-testid="nav-cfo-books">
          CFO Books · New
        </Link>
      </nav>
      <Link to="/analyze">
        <Button
          variant="outline"
          className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] rounded-md transition-all duration-300"
          data-testid="header-start-btn"
        >
          Start analysis
        </Button>
      </Link>
    </header>
  );
}

export function BrandMark() {
  return (
    <div className="h-9 w-9 rounded-lg bg-[#1F382E] flex items-center justify-center">
      <svg viewBox="0 0 100 100" className="h-5 w-5">
        <path d="M22 70 L42 50 L58 60 L78 32" stroke="#D4C9B6" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="78" cy="32" r="5" fill="#C26D5C" />
      </svg>
    </div>
  );
}

/**
 * BrandWordmark — single-image header lockup (mark + 'CFO Grade' wordmark).
 *
 * Uses the same PNG we ship to Google Ads so the brand reads identically
 * across the site and external placements. Served from /ads/ so it caches
 * aggressively and never costs a re-render.
 *
 * Defaults: 144x36 (h-9). Pass `size="sm"` for compact spots (footers,
 * dialogs) or `size="lg"` for hero placements.
 */
export function BrandWordmark({ size = "md", className = "" }) {
  const dims =
    size === "sm" ? "h-7 w-auto" :
    size === "lg" ? "h-12 w-auto" :
    "h-9 w-auto";
  return (
    <img
      src="/ads/logo_landscape_1200x300.png"
      alt="CFO Grade"
      className={`${dims} ${className}`}
      width="144"
      height="36"
      draggable="false"
    />
  );
}

function Hero({ onStart }) {
  return (
    <section className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7 fade-up">
        <span
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5C665D] border border-[#E5E0D8] bg-white/70 rounded-full px-3 py-1.5"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#C26D5C]" />
          A reading of your numbers
        </span>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#1F382E] leading-[1.02] tracking-tight mt-6">
          What grade would a CFO
          <span className="italic text-[#C26D5C]"> give your business?</span>
        </h1>
        <p className="text-base sm:text-lg text-[#5C665D] leading-relaxed mt-6 max-w-xl">
          Drop in your numbers — in any currency — or upload a sheet, and get your
          <strong> CFO Grade</strong>, a full ratio dashboard, peer benchmarks, a
          <strong> business-valuation range</strong> and a plain-English memo. Read it as
          an owner planning the next quarter, or as an analyst sizing up a borrower.
          Works for businesses anywhere.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Button
            onClick={onStart}
            data-testid="hero-cta-start"
            className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-12 px-7 transition-all duration-300"
          >
            Run a free analysis
            <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
          </Button>
          <Link to="/sample-report">
            <Button
              variant="ghost"
              className="text-[#1F382E] hover:bg-[#D4C9B6]/40 rounded-md h-12 px-6 transition-all duration-300"
              data-testid="hero-cta-sample"
            >
              See a sample report
            </Button>
          </Link>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
          <Stat label="Ratios" value="24+" />
          <Stat label="Valuation" value="Included" />
          <Stat label="Avg. time" value="3 min" />
        </div>
      </div>

      <div className="lg:col-span-5 fade-up fade-up-delay-2">
        <PreviewCard />
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l border-[#E5E0D8] pl-4">
      <div className="font-serif-display text-3xl text-[#1F382E] leading-none tabular-nums">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-[#5C665D] mt-2">{label}</div>
    </div>
  );
}

function PreviewCard() {
  return (
    <div className="relative group">
      <div className="absolute -inset-3 bg-[#D4C9B6]/40 rounded-3xl blur-2xl pointer-events-none" />
      <Link
        to="/sample-report"
        className="relative block bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-7 shadow-sm transition-all duration-300 hover:border-[#1F382E]/30 hover:shadow-md hover:-translate-y-0.5"
        data-testid="landing-preview-card-link"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C665D]">Sample report</p>
            <h3 className="font-serif-display text-2xl text-[#1F382E] mt-1">Sunrise Bakery Co.</h3>
          </div>
          <span className="bg-[#2C5C43]/10 text-[#2C5C43] text-xs font-medium px-2.5 py-1 rounded-full">
            Healthy
          </span>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <Metric label="EBITDA" value="$226k" sub="18.1% margin" />
          <Metric label="DSCR" value="4.5x" sub="Lender comfort: ≥1.25" />
          <Metric label="Current Ratio" value="1.66" sub="Comfortable" />
          <Metric label="Debt / EBITDA" value="1.24x" sub="Low leverage" />
        </div>

        <div className="mt-6 pt-5 border-t border-[#E5E0D8]">
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C665D]">CFO Grade</p>
          <div className="flex items-end gap-3 mt-1">
            <div className="font-serif-display text-5xl text-[#1F382E] leading-none tabular-nums">78</div>
            <span className="text-sm text-[#5C665D] mb-1">/ 100</span>
          </div>
          <div className="mt-3 h-1.5 w-full bg-[#E5E0D8] rounded-full overflow-hidden">
            <div className="h-full bg-[#1F382E] rounded-full" style={{ width: "78%" }} />
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-dashed border-[#E5E0D8] flex items-center justify-between">
          <span className="text-xs text-[#5C665D]">Click to open the full sample</span>
          <span className="text-xs text-[#C26D5C] font-medium inline-flex items-center gap-1 transition-transform group-hover:translate-x-0.5">
            Open <ArrowRight size={12} strokeWidth={1.5} />
          </span>
        </div>
      </Link>
    </div>
  );
}

function Metric({ label, value, sub }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">{label}</p>
      <div className="font-serif-display text-2xl text-[#1F382E] mt-1 tabular-nums">{value}</div>
      <p className="text-xs text-[#5C665D] mt-0.5">{sub}</p>
    </div>
  );
}

function Features() {
  return (
    <section id="features" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 border-t border-[#E5E0D8]">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">What it measures</p>
          <h2 className="font-serif-display text-4xl text-[#1F382E] leading-tight mt-3">
            The ratios bankers, accountants and acquirers actually look at.
          </h2>
          <p className="text-[#5C665D] leading-relaxed mt-4">
            We compute the full set — profitability, liquidity, solvency, efficiency, and
            cash flow — and explain what each one is whispering about your business.
          </p>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className={`bg-white border border-[#E5E0D8] rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-sm fade-up fade-up-delay-${(i % 5) + 1}`}
            >
              <f.icon size={20} strokeWidth={1.5} className="text-[#C26D5C]" />
              <h3 className="font-serif-display text-xl text-[#1F382E] mt-4">{f.title}</h3>
              <p className="text-sm text-[#5C665D] mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Persona() {
  return (
    <section id="who" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 border-t border-[#E5E0D8]">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative rounded-2xl overflow-hidden border border-[#E5E0D8] aspect-[4/5]">
            <img
              src="https://images.unsplash.com/photo-1773146916231-e6aeae932080?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBkZXNrfGVufDB8fHx8MTc3ODM3Njk5OXww&ixlib=rb-4.1.0&q=85"
              alt="A small business owner reviewing finances at her desk"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">Made for the operator</p>
          <h2 className="font-serif-display text-4xl text-[#1F382E] leading-tight mt-3">
            Built for the owner who runs the business <em>and</em> the books.
          </h2>
          <ul className="mt-6 space-y-4 text-[#1A1A1A]">
            {[
              "Family-owned shops trying to understand whether next quarter's loan payment is safe.",
              "Restaurant and service operators who want to know which margin to chase first.",
              "Bookkeepers and fractional CFOs who need a fast read for client conversations.",
              "Founders preparing for an SBA loan or a credit facility renewal.",
            ].map((t) => (
              <li key={t} className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#C26D5C] shrink-0" />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function FooterCta({ onStart }) {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
      <div className="bg-[#1F382E] rounded-2xl p-10 sm:p-14 text-[#F7F5F0] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #C26D5C 0%, transparent 40%), radial-gradient(circle at 80% 80%, #D4C9B6 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-3xl">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">
            Read the room — your financial room.
          </h2>
          <p className="text-[#D4C9B6] mt-4 leading-relaxed">
            No account. No card. Just an honest read of where you stand and what to do next.
          </p>
          <Button
            onClick={onStart}
            data-testid="footer-cta-start"
            className="mt-8 bg-[#C26D5C] hover:bg-[#a85a4b] text-white rounded-md h-12 px-7 transition-all duration-300"
          >
            Start your analysis <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-[#E5E0D8] py-10 max-w-7xl mx-auto px-6 sm:px-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-[#5C665D]">
      <div className="flex items-center gap-2">
        <BrandMark />
        <span>© {new Date().getFullYear()} CFO Grade. Educational insights for business owners — not financial advice.</span>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Link
          to="/valuation"
          className="underline underline-offset-2 hover:text-[#1F382E] transition-colors"
          data-testid="site-footnote-valuation-link"
        >
          Valuation calculator
        </Link>
        <span className="text-[#5C665D]/50">·</span>
        <Link
          to="/benchmarks"
          className="underline underline-offset-2 hover:text-[#1F382E] transition-colors"
          data-testid="site-footnote-benchmarks-link"
        >
          Industry benchmarks
        </Link>
        <span className="text-[#5C665D]/50">·</span>
        <Link to="/disclaimer" className="underline underline-offset-2 hover:text-[#1F382E] transition-colors">Full terms</Link>
      </div>
    </footer>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: ClipboardPaste,
      title: "Paste your numbers",
      body: "Type in a friendly form, or upload a CSV, Excel, or PDF of your P&L and balance sheet. As few as six inputs gets you a real report.",
    },
    {
      icon: BarChart3,
      title: "Get your grade",
      body: "In seconds: 24+ ratios, an A+ to F letter grade, and industry-aware benchmarks across 16 sectors and 15 regions.",
    },
    {
      icon: BookOpenCheck,
      title: "Read the memo",
      body: "Plain-English read of what's working and what to fix next quarter. Owner-operator lens or analyst lens — your choice.",
    },
  ];
  return (
    <section id="how" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 border-t border-[#E5E0D8]" data-testid="how-it-works-section">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">How it works</p>
          <h2 className="font-serif-display text-4xl text-[#1F382E] leading-tight mt-3">
            Three steps. No spreadsheet pain.
          </h2>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {steps.map((s, i) => (
          <div
            key={s.title}
            className="bg-white border border-[#E5E0D8] rounded-xl p-7 relative transition-all duration-300 hover:-translate-y-1 hover:shadow-sm"
            data-testid={`how-step-${i + 1}`}
          >
            <div className="font-serif-display text-5xl text-[#D4C9B6] leading-none absolute top-5 right-6 tabular-nums">
              {String(i + 1).padStart(2, "0")}
            </div>
            <s.icon size={22} strokeWidth={1.5} className="text-[#C26D5C]" />
            <h3 className="font-serif-display text-xl text-[#1F382E] mt-4 tracking-tight">{s.title}</h3>
            <p className="text-sm text-[#5C665D] mt-2 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function IndustryStrip() {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-14 border-t border-[#E5E0D8]" data-testid="industry-strip">
      <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D] text-center">
        Built with benchmarks across 16 industries
      </p>
      <div className="mt-8 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-5 sm:gap-6 items-center">
        {INDUSTRIES.map((ind) => (
          <div
            key={ind.label}
            className="flex flex-col items-center gap-2 text-[#5C665D] hover:text-[#1F382E] transition-colors"
            data-testid={`industry-${ind.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <ind.icon size={20} strokeWidth={1.5} />
            <span className="text-[11px] uppercase tracking-[0.16em] text-center leading-tight">
              {ind.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Voices() {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 border-t border-[#E5E0D8]" data-testid="voices-section">
      <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">Why people use it</p>
      <h2 className="font-serif-display text-4xl text-[#1F382E] leading-tight mt-3 max-w-2xl">
        Bookkeepers, owners, and analysts have all found different things to love.
      </h2>
      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {VOICES.map((v, i) => (
          <figure
            key={i}
            className="bg-white border border-[#E5E0D8] rounded-xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-sm"
            data-testid={`voice-${i}`}
          >
            <span className="font-serif-display text-4xl text-[#C26D5C] leading-none block">“</span>
            <blockquote className="text-[15px] text-[#1A1A1A] leading-relaxed mt-2">
              {v.quote}
            </blockquote>
            <figcaption className="text-xs uppercase tracking-[0.16em] text-[#5C665D] mt-5">
              — {v.role}
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="text-[11px] text-[#5C665D] mt-6">
        Composite voices from real user feedback during beta. Identifying details changed.
      </p>
    </section>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section id="faq" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 border-t border-[#E5E0D8]" data-testid="faq-section">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">FAQ</p>
          <h2 className="font-serif-display text-4xl text-[#1F382E] leading-tight mt-3">
            The honest answers, before you click anything.
          </h2>
          <p className="text-[#5C665D] leading-relaxed mt-4">
            Still wondering something?{" "}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("cfograde:open-feedback"))}
              className="underline underline-offset-4 hover:text-[#1F382E] transition-colors"
              data-testid="landing-faq-open-feedback"
            >
              Send us a note
            </button>
            {" "}— we read every one (no email required).
          </p>
        </div>
        <div className="lg:col-span-8">
          <div className="divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
            {FAQS.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={item.q}>
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="w-full flex items-start justify-between gap-6 py-5 text-left hover:bg-[#E5E0D8]/20 -mx-3 px-3 rounded transition-colors"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-serif-display text-lg sm:text-xl text-[#1F382E] tracking-tight">
                      {item.q}
                    </span>
                    <span className="mt-1.5 text-[#5C665D] shrink-0">
                      {isOpen ? <Minus size={18} strokeWidth={1.5} /> : <Plus size={18} strokeWidth={1.5} />}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="pb-6 pr-8" data-testid={`faq-answer-${i}`}>
                      <p className="text-[15px] text-[#1A1A1A] leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
